import React, { useState, useEffect } from 'react';
import { useClient } from 'jsonapi-react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { checkIncludeProfiles } from 'src/app/utils/getAuthorityLevel';
import { useAuditorium } from 'src/app/hooks/useAuditorium';
import { Link } from 'react-router-dom';
import { useSession } from 'src/app/hooks/useSession';
import { useNavigate } from 'react-router-dom';
import { useCourse } from 'src/app/hooks/useCourse';
import ViewAuditorium from './ViewAuditorium';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import AuditoriumCard from './AuditoriumCard';
import CreateAuditorium from './CreateAuditorium';
import Onboarding from 'src/app/components/Onboarding';
import ParticipantsModal from './ParticipantsModal';
import getPermission from 'src/app/utils/getPermission';
import ActionCable from 'actioncable';
import moment from 'moment';
import { DashboardContent } from '@/layouts/dashboard';
import { Filter, FilterActions, FilterContent, FilterInput, FilterSelectNative } from '@/components/custom/filter';
import { FiSearch } from 'react-icons/fi';
import { Button } from '@/components/ui/button';
import { CardWrapper } from '@/components/ui/card';

const TEN_SECONDS_IN_MILLISECONDS = 10 * 1000;

function Auditorium(props) {
  const navigate = useNavigate();
  if (!getPermission('Visualizar monitorias', 'Monitorias')) {
    return null;
  }
  const { path, uri } = props;
  const { getAuditoriums, loading, auditoriums } = useAuditorium();
  const { courses, getSimpleCourses } = useCourse();
  const [filteredAuditoriums, setFilteredAuditoriums] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [activeAuditorium, setActiveAuditorium] = useState();
  const [messageChannel, setMessageChannel] = useState(null);
  const { session } = useSession();
  const isAdmin = session.user.profileNames.includes('Administrador');
  const { t } = useTranslation();
  const client = useClient();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const profiles = session?.user?.profileNames || (session?.user && session?.user['profile-names']) || [];

  const apiBaseUrl = client.config.url;
  const cableUrl = apiBaseUrl.replace(/^http(s)?:\/\/(.*?)\/api\/v1\/?$/, 'wss://$2/cable');
  const cable = ActionCable.createConsumer(cableUrl);

  const filters = useFormik({
    initialValues: {
      courses: 'blank',
      gre: 'blank',
      school: 'blank',
      auditorium_name: ''
    }
  });

  if (auditoriums.length === 1 && !isAdmin) {
    navigate(`/auditorio/${auditoriums[0].id}`, { state: { singleAuditorium: true } });
  }

  useEffect(() => {
    if (messageChannel) {
      const auditoriumsStatusChanged = filteredAuditoriums.map(item => {
        if (item.id === Number(messageChannel.auditorium_id)) {
          return {
            ...item,
            status: 'Online',
            updated: moment().format()
          };
        } else {
          return { ...item, updated: null };
        }
      });

      setFilteredAuditoriums(auditoriumsStatusChanged);
    }
  }, [messageChannel]);

  useEffect(() => {
    getSimpleCourses();
    getAuditoriums();
  }, []);

  useEffect(() => {
    if (activeAuditorium) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [activeAuditorium]);

  useEffect(() => {
    const { courses, gre, school, auditorium_name } = filters.values;

    const filtered = auditoriums?.filter(item => {
      let filterResult = true;
      if (courses !== 'blank') {
        filterResult = filterResult && item?.['course-ids']?.includes(parseInt(courses));
      }
      if (gre !== 'blank') {
        filterResult = filterResult && item?.courses?.some(courseItem => courseItem['regional-id'] === parseInt(gre));
      }
      if (school !== 'blank') {
        filterResult = filterResult && item?.courses?.some(courseItem => courseItem['institution-id'] === parseInt(school));
      }

      if (auditorium_name !== '') {
        filterResult = filterResult && item?.name?.toLowerCase().includes(auditorium_name.toLowerCase());
      }

      return filterResult;
    });
    setFilteredAuditoriums(filtered);
  }, [filters.values, auditoriums]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const auditoriumsStatusChanged = filteredAuditoriums.map(item => {
        if (moment(item.updated).isBefore(moment().subtract(30, 'seconds'))) {
          return {
            ...item,
            status: 'Offline'
          };
        } else {
          return item;
        }
      });

      if (auditoriumsStatusChanged.length) {
        setFilteredAuditoriums(auditoriumsStatusChanged);
      }
    }, TEN_SECONDS_IN_MILLISECONDS);

    return () => clearInterval(intervalId);
  }, [filteredAuditoriums]);

  useEffect(() => {
    const subscription = cable.subscriptions.create(
      {
        channel: 'AuditoriumStatusChannel'
      },
      {
        received: data => {
          setMessageChannel(data);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [cableUrl]);

  const breadCrumbs = {
    title: 'Comunidade',
    nav: [
      {
        route: uri,
        name: 'Monitoria',
        isActive: true
      }
    ]
  };

  return (
    <>
      <DashboardContent
        variant="plain"
        breadcrumbs={breadCrumbs}
      >
        {!checkIncludeProfiles({ only: ['student'] }) ? (
          <Filter>
            <FilterContent>
              {/* <FilterSelectNative
                label={'Selecione uma GRE'}
                blankLabel={t('filter.blankLabelAll')}
                value={filters.values.gre}
                onChange={e => {
                  filters.setFieldValue('gre', e.target.value);
                }}
                options={regionals}
              /> */}
              {/* <FilterSelectNative
                label={'Selecione uma escola'}
                blankLabel={t('filter.blankLabelAll')}
                value={filters.values.school}
                onChange={e => {
                  filters.setFieldValue('school', e.target.value);
                }}
                options={istitutions}
              /> */}
              <FilterSelectNative
                label={t('filter.schoolYear')}
                blankLabel={t('filter.blankLabelAll')}
                value={filters.values.courses}
                onChange={e => {
                  filters.setFieldValue('courses', e.target.value);
                }}
                options={courses}
              />
            </FilterContent>

            {getPermission('Criar monitorias', 'Monitorias') && (
              <FilterActions>
                <Button
                  wide
                  asChild
                >
                  <Link to={`/auditorio/criar/nome`}>{t('auditorium.newAuditorium')}</Link>
                </Button>
              </FilterActions>
            )}
          </Filter>
        ) : (
          <Filter>
            <FilterContent>
              <FilterInput
                label={t('materials.search')}
                endIcon={FiSearch}
                placeholder="Pesquisar..."
                type="search"
                name="auditoriumSearch"
                value={filters.values.auditorium_name}
                onChange={e => filters.setFieldValue('auditorium_name', e.target.value)}
              />
              <FilterSelectNative
                label={t('filter.labelSelectCourse')}
                blankLabel={t('filter.blankLabelAll')}
                value={filters.values.courses}
                onChange={e => {
                  filters.setFieldValue('courses', e.target.value);
                }}
                options={courses}
              />
            </FilterContent>
          </Filter>
        )}

        {loading && <Loader />}

        {filteredAuditoriums.length === 0 && !loading && <EmptyState type="content" />}

        {!loading && (
          <div className="animate-fade-in">
            <CardWrapper>
              {filteredAuditoriums?.map(auditorium => (
                <AuditoriumCard
                  auditorium={auditorium}
                  setActiveAuditorium={setActiveAuditorium}
                  key={auditorium.id}
                />
              ))}
            </CardWrapper>
          </div>
        )}
      </DashboardContent>

      <Onboarding
        show={showOnboarding}
        setShowOnboarding={setShowOnboarding}
        onClose={() => setShowOnboarding(false)}
        path={path}
        profiles={profiles}
        title={t('onboarding.titleAuditorium')}
      />

      <ParticipantsModal
        showModal={showModal}
        onClose={() => setShowModal(false)}
        auditorium={activeAuditorium}
      />
    </>
  );
}

export { Auditorium as default, ViewAuditorium, CreateAuditorium };
